
import React from 'react';
import { McButton, McCard } from '@maersk-global/mds-react-wrapper/components-core';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NotificationProvider } from './context/NotificationContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import MainLayout from './components/Layout/MainLayout';
import LandingPage from './pages/LandingPage';
import QueriesPage from './pages/QueriesPage';
import CreateQueryPage from './pages/CreateQueryPage';

import QueryDetailPage from './pages/QueryDetailPage';
import QueryEditPage from './pages/QueryEditPage';
import SchedulesPage from './pages/SchedulesPage';
import HomePage from './pages/HomePage';
import RegionsPage from './pages/RegionsPage';
import DeveloperDocsPage from './pages/DeveloperDocsPage';
import FAQPage from './pages/FAQPage';
import AgentsPage from './pages/AgentsPage';
import CacheManagementPage from './pages/CacheManagementPage';
import UserQuotaManagementPage from './pages/UserQuotaManagementPage';
import ServerRegistryPage from './pages/ServerRegistryPage';
import RefDataViewerPage from './pages/RefDataViewerPage';
import FullScreenLoader from './components/Common/FullScreenLoader';
import { roleConfig } from './config/roles';

// Create React Query client with error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
    mutations: {
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* MDS: Ensure Foundations CSS is included globally and <html class="mds"> is set */}
      <NotificationProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </NotificationProvider>
    </QueryClientProvider>
  );
}

const AuthError: React.FC<{ message: string; retry: () => Promise<void> }> = ({ message, retry }) => (
  <McCard style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', textAlign: 'center', paddingInline: '0.75rem' }}>
    <h6 style={{ color: 'var(--mds-color-error-foreground, #b00020)' }}>{message}</h6>
    <p style={{ color: 'var(--mds-color-text-secondary, #666)' }}>
      Please try signing in again. If the issue persists, verify your Azure AD configuration.
    </p>
    <McButton variant="filled" appearance="primary" onClick={retry}>
      Retry Sign-in
    </McButton>
  </McCard>
);

const AccessDenied: React.FC<{ role?: string }> = ({ role }) => (
  <McCard style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', textAlign: 'center', paddingInline: '0.75rem' }}>
    <h6>Access Restricted</h6>
    <p style={{ color: 'var(--mds-color-text-secondary, #666)' }}>
      You do not have permission to view this section{role ? ` (requires ${role})` : ''}.
    </p>
  </McCard>
);

const RequireRole: React.FC<{ role: string | string[]; children: React.ReactElement }> = ({ role, children }) => {
  const { hasRole } = useAuth();
  if (!hasRole(role)) {
    return <AccessDenied role={Array.isArray(role) ? role.join(', ') : role} />;
  }
  return children;
};

const AppContent = () => {
  const { loading, isAuthenticated, authEnabled, error, login } = useAuth();
  const navigate = useNavigate();

  if (authEnabled) {
    if (loading) {
      return <FullScreenLoader message="Initializing Sentinel Data Hub..." />;
    }

    if (error) {
      return <AuthError message={error} retry={login} />;
    }

    if (!isAuthenticated) {
      return <LandingPage />;
    }
  }

  return (
    <MainLayout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        {/* ... existing routes ... */}
        <Route path="/queries" element={<QueriesPage />} />
        <Route path="/queries/create" element={<CreateQueryPage />} />

        <Route path="/queries/:queryId" element={<QueryDetailPage />} />
        <Route
          path="/queries/:queryId/edit"
          element={
            <RequireRole role={roleConfig.manageQueries}>
              <QueryEditPage />
            </RequireRole>
          }
        />
        <Route
          path="/schedules"
          element={
            <RequireRole role={roleConfig.viewSchedules}>
              <SchedulesPage />
            </RequireRole>
          }
        />
        <Route
          path="/faq"
          element={
            <RequireRole role={roleConfig.viewFaq}>
              <FAQPage />
            </RequireRole>
          }
        />
        <Route
          path="/agents"
          element={
            <RequireRole role={roleConfig.viewAgents}>
              <AgentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/developer-docs"
          element={
            <RequireRole role={roleConfig.viewDevDocs}>
              <DeveloperDocsPage />
            </RequireRole>
          }
        />
        <Route
          path="/regions"
          element={
            <RequireRole role={roleConfig.viewRegions}>
              <RegionsPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/cache"
          element={
            <RequireRole role={roleConfig.manageCache}>
              <CacheManagementPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/user-quotas"
          element={
            <RequireRole role={roleConfig.manageUserQuota}>
              <UserQuotaManagementPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/server-registry"
          element={
            <RequireRole role={roleConfig.viewServerRegistry}>
              <ServerRegistryPage />
            </RequireRole>
          }
        />
        <Route
          path="/ref-data"
          element={
            <RequireRole role={roleConfig.viewRefData}>
              <RefDataViewerPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/ref-data"
          element={
            <RequireRole role={roleConfig.manageRefData}>
              <RefDataViewerPage />
            </RequireRole>
          }
        />
      </Routes>
    </MainLayout>
  );
};

export default App;
